import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Trash2, Download, Pause, Play, Filter, ChevronDown, ChevronUp } from 'lucide-react';

// Global log store so logs persist across component mounts
const globalLogStore = [];
const logListeners = new Set();
let isIntercepted = false;
const originalConsole = {
  log: console.log,
  error: console.error,
  warn: console.warn,
  info: console.info,
};

function notifyListeners() {
  logListeners.forEach((fn) => fn([...globalLogStore]));
}

function formatArg(arg) {
  if (arg === null) return 'null';
  if (arg === undefined) return 'undefined';
  if (typeof arg === 'string') return arg;
  try {
    return JSON.stringify(arg, null, 2);
  } catch {
    return String(arg);
  }
}

function interceptConsole() {
  if (isIntercepted) return;
  isIntercepted = true;

  ['log', 'error', 'warn', 'info'].forEach((method) => {
    console[method] = (...args) => {
      // Call original
      originalConsole[method](...args);

      // Store the log entry
      const entry = {
        id: Date.now() + Math.random(),
        type: method,
        message: args.map(formatArg).join(' '),
        timestamp: new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 }),
        fullTimestamp: new Date().toISOString(),
      };

      globalLogStore.push(entry);
      // Keep max 2000 entries
      if (globalLogStore.length > 2000) {
        globalLogStore.splice(0, globalLogStore.length - 2000);
      }
      notifyListeners();
    };
  });
}

// Start intercepting immediately when this module loads
interceptConsole();

const DebugConsole = () => {
  const [logs, setLogs] = useState([...globalLogStore]);
  const [paused, setPaused] = useState(false);
  const [filterType, setFilterType] = useState('all'); // all, log, error, warn, info
  const [searchText, setSearchText] = useState('');
  const [autoScroll, setAutoScroll] = useState(true);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const logContainerRef = useRef(null);
  const pausedLogsRef = useRef([...globalLogStore]);

  useEffect(() => {
    const handler = (newLogs) => {
      if (!paused) {
        setLogs(newLogs);
      } else {
        pausedLogsRef.current = newLogs;
      }
    };
    logListeners.add(handler);
    return () => logListeners.delete(handler);
  }, [paused]);

  useEffect(() => {
    if (autoScroll && logContainerRef.current && !paused) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs, autoScroll, paused]);

  const handleResume = useCallback(() => {
    setPaused(false);
    setLogs([...pausedLogsRef.current]);
  }, []);

  const clearLogs = useCallback(() => {
    globalLogStore.length = 0;
    setLogs([]);
    pausedLogsRef.current = [];
  }, []);

  const downloadLogs = useCallback(() => {
    const content = globalLogStore
      .map((l) => `[${l.fullTimestamp}] [${l.type.toUpperCase()}] ${l.message}`)
      .join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `debug-logs-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  const toggleRow = useCallback((id) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const filteredLogs = logs.filter((entry) => {
    if (filterType !== 'all' && entry.type !== filterType) return false;
    if (searchText && !entry.message.toLowerCase().includes(searchText.toLowerCase())) return false;
    return true;
  });

  const typeColors = {
    log: 'text-gray-300',
    error: 'text-red-400',
    warn: 'text-yellow-400',
    info: 'text-blue-400',
  };

  const typeBgColors = {
    log: '',
    error: 'bg-red-950/30',
    warn: 'bg-yellow-950/30',
    info: 'bg-blue-950/30',
  };

  const typeBadge = {
    log: 'bg-gray-700 text-gray-300',
    error: 'bg-red-900/60 text-red-300',
    warn: 'bg-yellow-900/60 text-yellow-300',
    info: 'bg-blue-900/60 text-blue-300',
  };

  const counts = {
    all: logs.length,
    log: logs.filter((l) => l.type === 'log').length,
    error: logs.filter((l) => l.type === 'error').length,
    warn: logs.filter((l) => l.type === 'warn').length,
    info: logs.filter((l) => l.type === 'info').length,
  };

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Debug Console</h1>
        <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
          Live console output from Smart Table &amp; Untapped Opportunity
        </p>
      </div>

      {/* Toolbar */}
      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-t-xl p-3 flex flex-wrap items-center gap-2">
        {/* Filter tabs */}
        <div className="flex items-center gap-1 mr-2">
          {['all', 'log', 'error', 'warn', 'info'].map((type) => (
            <button
              key={type}
              onClick={() => setFilterType(type)}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                filterType === type
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-slate-700'
              }`}
            >
              {type === 'all' ? 'All' : type.charAt(0).toUpperCase() + type.slice(1)}
              {counts[type] > 0 && (
                <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] ${
                  filterType === type ? 'bg-blue-500 text-white' : 'bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-slate-400'
                }`}>
                  {counts[type]}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="flex-1 min-w-[200px]">
          <input
            type="text"
            placeholder="Filter logs..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="w-full px-3 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => paused ? handleResume() : setPaused(true)}
            className={`p-1.5 rounded-lg transition-colors ${
              paused
                ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50'
                : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-slate-700'
            }`}
            title={paused ? 'Resume' : 'Pause'}
          >
            {paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
          </button>
          <button
            onClick={downloadLogs}
            className="p-1.5 rounded-lg bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-400 hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors"
            title="Download logs"
          >
            <Download className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={clearLogs}
            className="p-1.5 rounded-lg bg-gray-100 dark:bg-slate-800 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
            title="Clear logs"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Log output area */}
      <div
        ref={logContainerRef}
        className="bg-gray-950 border border-t-0 border-gray-200 dark:border-slate-700 rounded-b-xl font-mono text-xs overflow-auto"
        style={{ height: 'calc(100vh - 250px)', minHeight: '400px' }}
      >
        {paused && (
          <div className="sticky top-0 z-10 bg-yellow-900/80 text-yellow-200 text-center py-1 text-[11px] font-medium">
            Paused — {pausedLogsRef.current.length - logs.length} new logs buffered
          </div>
        )}

        {filteredLogs.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <Filter className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p>{logs.length === 0 ? 'No logs yet. Navigate to Smart Table or Untapped Opportunity to see output.' : 'No logs match the current filter.'}</p>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-800/50">
            {filteredLogs.map((entry) => {
              const isLong = entry.message.length > 200;
              const isExpanded = expandedRows.has(entry.id);

              return (
                <div
                  key={entry.id}
                  className={`px-3 py-1.5 hover:bg-gray-900/50 flex items-start gap-2 ${typeBgColors[entry.type]}`}
                  onClick={() => isLong && toggleRow(entry.id)}
                  style={{ cursor: isLong ? 'pointer' : 'default' }}
                >
                  <span className="text-gray-600 flex-shrink-0 select-none">{entry.timestamp}</span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${typeBadge[entry.type]}`}>
                    {entry.type.toUpperCase().padEnd(5)}
                  </span>
                  <span className={`${typeColors[entry.type]} break-all ${!isExpanded && isLong ? 'line-clamp-2' : ''}`} style={{ whiteSpace: 'pre-wrap' }}>
                    {entry.message}
                  </span>
                  {isLong && (
                    <span className="flex-shrink-0 text-gray-600 mt-0.5">
                      {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Status bar */}
      <div className="mt-2 flex items-center justify-between text-[11px] text-gray-400 dark:text-slate-500">
        <span>
          Showing {filteredLogs.length} of {logs.length} entries
          {counts.error > 0 && <span className="text-red-400 ml-2">{counts.error} error{counts.error > 1 ? 's' : ''}</span>}
          {counts.warn > 0 && <span className="text-yellow-400 ml-2">{counts.warn} warning{counts.warn > 1 ? 's' : ''}</span>}
        </span>
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            checked={autoScroll}
            onChange={(e) => setAutoScroll(e.target.checked)}
            className="w-3 h-3 rounded"
          />
          Auto-scroll
        </label>
      </div>
    </div>
  );
};

export default DebugConsole;
